import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from '@brokerportal/common/auth';
import { VenusDemandMapping } from '@brokerportal/common/auth/demands.const';
import { BrokerPortalFeatureCodes, ProductTag } from '@brokerportal/common/enums';
import { BrokerConfiguration, PartnerConfiguration, UserConfiguration } from '@brokerportal/common/models';
import {
    FeatureFlagService,
    MediaReplayService,
    PortalConfigurationService,
    QuoteService
} from '@brokerportal/common/services';
import { scrollToFirstInvalidControl, WABSLoanLimit } from '@brokerportal/common/utils';
import { ERROR_SNACKBAR_DURATION, extractErrorMessage } from '@brokerportal/common/utils/error.utils';
import BpValidators from '@brokerportal/common/validators';
import {
    InterestFreeLinkGeneratorComponent,
    InterestFreeLinkPrefillData,
    SendLinkModalData
} from '@brokerportal/modules/application/create/components/interest-free-link-generator/interest-free-link-generator.component';
import {
    CecPackageModel,
    NETWORK_VPP_MAP,
    NetworkArea,
    VPP_NETWORK_MAP,
    WA_NETWORK_AREAS
} from '@brokerportal/modules/quote/shared/models';
import { NotificationType } from '@brokerportal/modules/quote/shared/models/quoteV2.model';
import { RegexPatterns } from 'app/constants';
import { Decimal } from 'decimal.js';
import { combineLatest, merge, Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';
import {
    ENERGY_RETAILER_LABELS,
    InstallationType,
    PackagesModel,
    QuoteFormCrudMode,
    QuoteFormModel,
    QuoteReviewModel,
    RebateProgram,
    SapnMacActivityType,
    Scheme
} from '../../../models';
import { QuoteStatusService } from '../../../services';
import { ConfirmDialog } from '../../confirm-dialog/confirm-dialog.component';

export interface PackageSelectionEvent {
    category: 'battery' | 'inverter';
    pkg: CecPackageModel;
}

const streetNameRegex = /^[ '&a-zA-Z-]{2,30}$/;

@Component({
    selector: 'bp-wa-quote-form',
    templateUrl: './wa-quote-form.component.html',
    styleUrls: ['../quote-form.component.scss', './wa-quote-form.component.scss']
})
export class WaQuoteFormComponent implements OnInit, OnDestroy {
    @Input() formMode: QuoteFormCrudMode;
    @Output() formModeChange = new EventEmitter<QuoteFormCrudMode>();
    @Input() model: QuoteFormModel;
    @Input() quoteId: string = '';
    @Input() quoteStatus: string = '';
    @Input() quoteNumber?: string;
    @Input() applicationId?: string;
    @Input()
    set selectedPackages(value: PackagesModel<CecPackageModel>) {
        this._localSelectedPackages = {
            battery: value?.battery || null,
            inverter: value?.inverter || null
        };
    }

    private _localSelectedPackages: PackagesModel<CecPackageModel> = {
        battery: null,
        inverter: null
    };

    get _selectedPackages(): PackagesModel<CecPackageModel> {
        return this._localSelectedPackages || {};
    }

    @Output() onSubmit = new EventEmitter<QuoteReviewModel>();
    @Output() onCancelled = new EventEmitter<void>();
    @Output() onToConfirmInstallation = new EventEmitter<void>();
    @Output() packageSelected = new EventEmitter<PackageSelectionEvent>();

    //#region Template reference variables
    //TODO: Use TypeScript's `protected` accessor directly after migrating to Angular 14,  which supports protected member reference in the template.
    /** @protected */
    readonly QuoteFormCrudMode = QuoteFormCrudMode;
    /** @protected */
    readonly _networkAreas = WA_NETWORK_AREAS;
    /** @protected */
    _filteredVpps: { value: string; label: string }[] = [];
    /** @protected */
    readonly ENERGY_RETAILER_LABELS = ENERGY_RETAILER_LABELS;
    /** @protected */
    readonly WABSLoanLimit = WABSLoanLimit;
    //#endregion Template reference variables

    form: FormGroup;

    private _loading: boolean;
    get loading() {
        return this._loading;
    }

    private _isMobile = false;
    get isMobile() {
        return this._isMobile;
    }

    private multipleBatteryEnabled = false;
    private resentQuoteButtonEnabled = false;
    private verificationSectionFlagEnabled = false;
    private allDocumentsProvided = false;
    private readonly actionPermissions = {
        canPatchQuote: false,
        canCancelQuote: false,
        canConfirmInstallation: false
    };

    private portalConfig: {
        partner: PartnerConfiguration;
        broker: BrokerConfiguration;
        user: UserConfiguration;
    };

    verificationStatuses = [
        'PendingInstallationVerification',
        'InstallationVerified',
        'InstallationConfirmedByCustomer'
    ];
    resendInstallEmailSent = false;

    private readonly destroyed$ = new Subject<void>();

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly dialog: MatDialog,
        private readonly snackBar: MatSnackBar,
        authService: AuthenticationService,
        mediaReplayService: MediaReplayService,
        private readonly portalConfigurationService: PortalConfigurationService,
        private readonly quoteService: QuoteService,
        private readonly quoteStatusService: QuoteStatusService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly elementRef: ElementRef
    ) {
        authService.hasDemand(VenusDemandMapping.CanCancelQuote).then(value => {
            this.actionPermissions.canCancelQuote = value;
        });
        authService.hasDemand(VenusDemandMapping.CanPatchQuote).then(value => {
            this.actionPermissions.canPatchQuote = value;
        });
        authService.hasDemand(VenusDemandMapping.CanConfirmInstallation).then(value => {
            this.actionPermissions.canConfirmInstallation = value;
        });

        mediaReplayService.isMobile$
            .pipe(takeUntil(this.destroyed$))
            .subscribe(isMobile => (this._isMobile = isMobile));
    }

    ngOnInit() {
        this.initForm();

        // Fill the form with the model data on View/Edit mode
        this.resetFormValues(false);

        this.setupFormBehaviours();
        this.setupSystemResetLogic();

        this.featureFlagService.isEnabled(BrokerPortalFeatureCodes.MultipleBatteryInput).subscribe(enabled => {
            if (enabled) {
                this.multipleBatteryEnabled = true;
            } else {
                this.multipleBatteryEnabled = false;
            }
        });

        this.featureFlagService.isEnabled(BrokerPortalFeatureCodes.EnableResendQuoteButton).subscribe(enabled => {
            if (enabled) {
                this.resentQuoteButtonEnabled = true;
            } else {
                this.resentQuoteButtonEnabled = false;
            }
        });

        this.featureFlagService
            .isEnabled(BrokerPortalFeatureCodes.VerificationStatusSectionForRebateApp)
            .subscribe(enabled => {
                this.verificationSectionFlagEnabled = !!enabled;
            });

        this.portalConfigurationService.get().subscribe(config => {
            this.portalConfig = null;
            if (!config) return;
            const broker = config.brokers?.find(
                broker => broker.productTag === ProductTag.RenewableInterestFree && broker.isDirect
            );
            if (!broker) return;
            this.portalConfig = {
                partner: config.partner,
                broker: broker,
                user: config.user
            };
        });

        this.getSubsidy();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    startEditing() {
        if (this.formMode === QuoteFormCrudMode.View) {
            this.formMode = QuoteFormCrudMode.Edit;
        }
    }

    cancelEditing() {
        this.resetFormValues();
        this.formMode = QuoteFormCrudMode.View;
        this.formModeChange.emit(QuoteFormCrudMode.View);
        this.getSubsidy();
    }

    cancelQuote() {
        this.dialog
            .open(ConfirmDialog, {
                data: {
                    title: 'Cancel Quote Record',
                    action: 'Are you sure you want to cancel this Quote Record?',
                    defaultNo: true
                }
            })
            .afterClosed()
            .subscribe(result => {
                if (result === 'confirm') {
                    this._loading = true;
                    this.quoteService.cancelQuote(this.quoteId).subscribe({
                        next: () => {
                            this._loading = false;
                            this.onCancelled.next();
                        },
                        error: err => {
                            this._loading = false;
                            const errorMessage = extractErrorMessage(err, {
                                defaultMessage: 'Failed to cancel the quote record. Please try again.'
                            });
                            this.snackBar.open(`Error cancelling Quote Record: ${errorMessage}`, 'Dismiss', {
                                duration: ERROR_SNACKBAR_DURATION
                            });
                            console.error('Error cancelling Quote Record:', err);
                        }
                    });
                }
            });
    }

    resendRebateQuote() {
        this.quoteService
            .resendQuoteEmail(this.quoteId, { notificationTypes: [NotificationType.ResendRebateEmail] })
            .subscribe({
                next: () => {
                    this.snackBar.open(`Rebate email resend successfully`, null, { duration: 5000 });
                },
                error: err => {
                    this.snackBar.open('Failed to resend rebate email', null, { duration: 5000 });
                    console.error(err);
                }
            });
    }

    showResendRebateButton(): boolean {
        return Boolean(
            this.resentQuoteButtonEnabled && this.quoteStatusService.isRebateQuoteResendable(this.quoteStatus)
        );
    }

    confirmInstallation() {
        this.onToConfirmInstallation.next();
    }

    applyForFinance() {
        if (!this.portalConfig) return;

        this.resendFinanceLink();
    }

    resendFinanceLink() {
        if (!this.portalConfig) return;

        const formValue = this.form.getRawValue();

        const prefillData: InterestFreeLinkPrefillData = {
            systemCost: this._getTotalSystemAndInstallationCost(),
            deposit: this._getDepositAmount(),
            clientName: {
                firstName: formValue.firstName,
                middleName: formValue.middleName,
                lastName: formValue.lastName
            },
            clientEmail: formValue.email,
            clientPhoneNumber: formValue.mobile,
            clientResidentialAddress: { ...formValue.installationAddress },
            totalIncomeLessThanThresholdConfirmed: formValue.isTotalIncomeLessThanThreshold
        };

        this._openFinanceDialog(prefillData);
    }

    get showVerificationSection(): boolean {
        return this.verificationSectionFlagEnabled && this.verificationStatuses.includes(this.quoteStatus || '');
    }

    verificationSectionConfig: { [status: string]: Array<{ text: string; color: string }> } = {
        PendingInstallationVerification: [
            { text: 'Our team is reviewing the installation documentation.', color: 'orange' }
        ],
        InstallationVerified: [
            { text: 'Our team has reviewed the installation documentation.', color: 'green' },
            {
                text:
                    'The energy provider will assess the compliance with the technical requirements of the installation.',
                color: 'orange'
            },
            { text: 'An email has been sent to the customer to confirm the installation.', color: 'orange' }
        ],
        InstallationConfirmedByCustomer: [
            { text: 'Our team has reviewed the installation documentation.', color: 'green' },
            { text: 'The customer has confirmed the installation.', color: 'green' },
            {
                text:
                    'The energy provider will assess the compliance with the technical requirements of the installation.',
                color: 'orange'
            }
        ]
    };

    get verificationSectionItems(): Array<{ text: string; color: string }> {
        return this.verificationSectionConfig[this.quoteStatus || ''] || [];
    }

    get resendInstallButtonLabel(): string {
        return this.resendInstallEmailSent ? 'Email Sent' : 'Re-send';
    }

    resendInstallationCompletedEmail() {
        this.quoteService
            .resendQuoteEmail(this.quoteId, {
                notificationTypes: [NotificationType.ResendInstallationCompletedEmail]
            })
            .subscribe({
                next: () => {
                    this.resendInstallEmailSent = true;
                    this.snackBar.open(`Installation confirmation email resent successfully`, null, { duration: 5000 });
                },
                error: err => {
                    this.snackBar.open('Failed to resend installation confirmation email', null, { duration: 5000 });
                    console.error(err);
                }
            });
    }

    private _openFinanceDialog(prefillData?: InterestFreeLinkPrefillData) {
        const formValue = this.form.getRawValue();
        const dialogData: SendLinkModalData = {
            productTag: ProductTag.RenewableInterestFree,
            partnerId: this.portalConfig.partner.id,
            brokerId: this.portalConfig.broker.id,
            brokerName: this.portalConfig.broker.name,
            brokerUserName: this.portalConfig.user.userName,
            renewableRebate: Scheme.WesternAustralia,
            additionalData: {
                'loan.subsidyAmount': formValue.activities[0].rebateAmount,
                'extraFieldsDict.quoteId': this.quoteId,
                'extraFieldsDict.quoteNumber': this.quoteNumber
            },
            prefillData: prefillData,
            isSystemCostDisabled: true
        };

        this.dialog.open<InterestFreeLinkGeneratorComponent, SendLinkModalData>(InterestFreeLinkGeneratorComponent, {
            width: '650px',
            maxWidth: '90vw',
            data: dialogData
        });
    }

    private setupFormBehaviours() {
        const networkAreaControl = this.form.get('networkArea');
        const installBatteryActivityCtrl = this.form.get('activities.0');
        const vppControl = this.form.get('activities.0.vpp');
        const numberOfBatteriesControl = this.form.get('numberOfBatteries');
        const batteryPackageControl = this.form.get('batteryPackage');
        const hasMultipleBatteriesControl = this.form.get('hasMultipleBatteries');

        this.setupDepositValidation();

        // Set up VPP dependencies
        networkAreaControl.valueChanges.pipe(startWith(networkAreaControl.value)).subscribe((network: NetworkArea) => {
            const currentVppValue = vppControl.value;
            vppControl.reset();

            this._filteredVpps = NETWORK_VPP_MAP[network] || [];

            if (network && this._filteredVpps.length > 0) {
                vppControl.enable();

                if (this._filteredVpps.length === 1) {
                    vppControl.setValue(this._filteredVpps[0].value);
                } else if (this._filteredVpps.some(v => v.value === currentVppValue)) {
                    vppControl.setValue(currentVppValue);
                }
            } else {
                vppControl.disable();
                this._filteredVpps = [];
            }
        });
        vppControl.valueChanges.pipe(startWith(vppControl.value)).subscribe((vpp: string) => {
            const batteryCtrl = this.form.get('batteryPackage');
            if (vpp) {
                batteryCtrl.enable();
            } else {
                batteryCtrl.disable();
            }
        });

        hasMultipleBatteriesControl.valueChanges
            .pipe(startWith(hasMultipleBatteriesControl.value))
            .subscribe((multipleBatteries: boolean) => {
                if (!multipleBatteries) {
                    if (numberOfBatteriesControl) {
                        numberOfBatteriesControl.setValue(1);
                    }
                }
            });

        if (numberOfBatteriesControl) {
            numberOfBatteriesControl.valueChanges
                .pipe(startWith(numberOfBatteriesControl.value))
                .subscribe((numberOfBatteriesCount: number) => {
                    if (
                        vppControl.value &&
                        batteryPackageControl.value &&
                        RegexPatterns.UUID.test(batteryPackageControl.value)
                    ) {
                        this.quoteService
                            .calculateSubsidy(batteryPackageControl.value, {
                                scope: Scheme.WesternAustralia,
                                isEnergyConcessionHolder: false,
                                vppOperator: vppControl.value,
                                numberOfBatteries: numberOfBatteriesCount
                            })
                            .subscribe(subsidyAmount => {
                                installBatteryActivityCtrl.get('rebateAmount').setValue(subsidyAmount);
                            });
                    } else {
                        installBatteryActivityCtrl.get('rebateAmount').setValue(null);
                    }
                });
        }

        combineLatest([
            installBatteryActivityCtrl.get('systemAndInstallationCost').valueChanges,
            installBatteryActivityCtrl.get('rebateAmount').valueChanges,
            this.form.get('customerDeposit').valueChanges.pipe(startWith(this.form.get('customerDeposit').value))
        ]).subscribe(([systemAndInstallationCost, rebateAmount, deposit]) => {
            const subtotal = +systemAndInstallationCost > 0 ? +systemAndInstallationCost - (+rebateAmount || 0) : 0;
            installBatteryActivityCtrl.get('subtotal').setValue(subtotal);
            this.form.get('balance').setValue(subtotal);
            const financeAmountCtrl = this.form.get('financeAmount');
            financeAmountCtrl.setValue(+subtotal > 0 ? +subtotal - (+deposit || 0) : 0);
            financeAmountCtrl.markAsTouched();
        });

        combineLatest([
            this.form.get('packagePrice').valueChanges,
            this.form.get('installationPrice').valueChanges
        ]).subscribe(([packagePrice, installationPrice]) => {
            const totalSystemAndInstallationCost = +packagePrice + +installationPrice;
            installBatteryActivityCtrl.get('systemAndInstallationCost').setValue(totalSystemAndInstallationCost);
        });

        batteryPackageControl.valueChanges.subscribe((packageId: string) => {
            if (vppControl.value && packageId && RegexPatterns.UUID.test(packageId)) {
                const numberOfBatteriesCount = numberOfBatteriesControl?.value ? numberOfBatteriesControl.value : 1;
                this.quoteService
                    .calculateSubsidy(packageId, {
                        scope: Scheme.WesternAustralia,
                        isEnergyConcessionHolder: false,
                        vppOperator: vppControl.value,
                        numberOfBatteries: numberOfBatteriesCount
                    })
                    .subscribe(subsidyAmount => {
                        installBatteryActivityCtrl.get('rebateAmount').setValue(subsidyAmount);
                    });
            } else {
                installBatteryActivityCtrl.get('rebateAmount').setValue(null);
            }
        });
    }

    private initForm() {
        this.form = this.formBuilder.group({
            firstName: ['', [Validators.required]],
            middleName: [''],
            lastName: ['', [Validators.required]],
            email: [
                { value: '', disabled: this.formMode !== QuoteFormCrudMode.Create },
                [Validators.required, Validators.email]
            ],
            mobile: [
                { value: '', disabled: this.formMode !== QuoteFormCrudMode.Create },
                [Validators.required, Validators.pattern('^[0-9]{10}$')]
            ],
            confirmCorrectCustomerDetails: [
                this.formMode === QuoteFormCrudMode.Create ? null : { value: true, disabled: true },
                [this.formMode === QuoteFormCrudMode.Create && Validators.requiredTrue]
            ],
            networkArea: [null, [Validators.required]],
            hasMultipleBatteries: [false],
            installationAddress: this.formBuilder.group(
                {
                    fullAddress: ['', [Validators.required, Validators.pattern(/^(.+(\sWA\s)\d{4})?$/)]],
                    unitNumber: ['', []],
                    streetNumber: ['', [Validators.required]],
                    street: ['', [Validators.required, Validators.pattern(streetNameRegex)]],
                    suburb: ['', [Validators.required, Validators.pattern(streetNameRegex)]],
                    postcode: [''],
                    state: ['', [Validators.required, BpValidators.acceptedValues(['WA'], true)]]
                },
                { validators: [Validators.required] }
            ),
            installationType: [[InstallationType.Battery]],
            nmi: [null, [Validators.required, Validators.pattern(RegexPatterns.Nmi.AllStates)]],
            activities: this.formBuilder.array([
                this.formBuilder.group({
                    activityType: [SapnMacActivityType.InstallBattery],
                    associatedRebate: [RebateProgram.WA_GOV],
                    vpp: [null, [Validators.required]],
                    systemAndInstallationCost: [
                        null,
                        [Validators.required, Validators.pattern(RegexPatterns.Currency)]
                    ],
                    rebateAmount: [null, [BpValidators.min(0, false)]],
                    subtotal: [null]
                })
            ]),
            batteryPackage: [null, [Validators.required]],
            packagePrice: [null, [Validators.required, Validators.pattern(RegexPatterns.Currency)]],
            inverterPackage: [null, [Validators.required]],
            hasExistingInverter: [null],
            installationPrice: [null, [Validators.required, Validators.pattern(RegexPatterns.Currency)]],
            optInFinance: [false],
            isEligibleOnActiveResidentialTariffs: [false],
            isSynergyComplianceConfirmed: [false],
            isSynergyCustomerConsentConfirmed: [
                false,
                [
                    BpValidators.conditionallyDriven(
                        'activities',
                        (_self, activitiesControl) => {
                            const activities = activitiesControl?.value;
                            const firstActivity = Array.isArray(activities) ? activities[0] : null;
                            return firstActivity?.vpp === 'Synergy';
                        },
                        [Validators.requiredTrue]
                    )
                ]
            ],
            balance: [
                null,
                [
                    BpValidators.conditionallyDriven('optInFinance', (self, driver) => driver?.value, [
                        BpValidators.min(0, false),
                        Validators.pattern(RegexPatterns.Currency)
                    ])
                ]
            ],
            customerDeposit: [null, [Validators.pattern(RegexPatterns.Currency), BpValidators.min(0, false)]],
            isTotalIncomeLessThanThreshold: [false],
            numberOfBatteries: [
                1,
                [Validators.required, Validators.pattern('^[0-9]{1,2}$'), Validators.min(1), Validators.max(25)]
            ],
            financeAmount: [null, [BpValidators.min(0)]]
        });
    }

    private resetFormValues(emitEvent = true) {
        if (!this.model) {
            return;
        }

        const firstActivity = this.model.activities?.[0];
        const vpp = firstActivity?.vpp;
        const networkArea = vpp ? VPP_NETWORK_MAP[vpp] : null;
        const subtotal = firstActivity?.subtotal ?? null;

        this.form.reset(
            {
                ...this.model,
                balance: subtotal,
                networkArea: networkArea,
                hasMultipleBatteries: this.model.numberOfBatteries > 1 ? true : false,
                installationAddress: {
                    ...this.model.installationAddress,
                    postcode: this.model.installationAddress?.postCode
                }
            },
            { emitEvent }
        );

        if (firstActivity) {
            const activityControl = this.form.get('activities.0');
            activityControl?.patchValue(
                {
                    ...firstActivity,
                    subtotal: subtotal
                },
                { emitEvent: false }
            );
        }

        const balanceControl = this.form.get('balance');
        balanceControl?.setValue(subtotal, { emitEvent: false });

        if (this.model.financeAmount !== undefined && this.model.financeAmount !== null) {
            const financeAmountControl = this.form.get('financeAmount');
            financeAmountControl?.setValue(this.model.financeAmount, { emitEvent: false });
        }
    }

    private getSubsidy() {
        if (this.quoteId && this.formMode !== QuoteFormCrudMode.Edit) {
            this._loading = true;
            this.quoteService.getSubsidy(this.quoteId).subscribe({
                next: subsidy => {
                    this._loading = false;
                    this.form.get('activities.0.rebateAmount').setValue(subsidy);
                    /**
                     * TODO: uncomment the following lines after confirming with Matt about
                     * recalculating the finance amount after getting subsidy
                     */
                    // this.form.get('packagePrice').updateValueAndValidity();
                    // this.form.get('installationPrice').updateValueAndValidity();
                    // this.form.get('customerDeposit').updateValueAndValidity();
                },
                error: err => {
                    this._loading = false;
                    const errorMessage = extractErrorMessage(err, {
                        defaultMessage: 'Failed to retrieve subsidy information. Please try again.'
                    });
                    this.snackBar.open(`Error getting subsidy: ${errorMessage}`, 'Dismiss', {
                        duration: ERROR_SNACKBAR_DURATION
                    });
                    console.error('Error getting subsidy:', err);
                }
            });
        }
    }

    private resetSystemAndPriceControls(): void {
        this.form.get('batteryPackage').reset();
        this.form.get('inverterPackage').reset();

        this.form.get('packagePrice').reset();
        this.form.get('installationPrice').reset();

        this._localSelectedPackages.battery = null;
        this._localSelectedPackages.inverter = null;
    }

    private setupDepositValidation(): void {
        const customerDepositControl = this.form.get('customerDeposit');
        const optInFinanceControl = this.form.get('optInFinance');
        const balanceControl = this.form.get('balance');

        combineLatest([
            balanceControl.valueChanges.pipe(startWith(balanceControl.value)),
            optInFinanceControl.valueChanges.pipe(startWith(optInFinanceControl.value))
        ])
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                customerDepositControl.updateValueAndValidity({ emitEvent: false });
            });

        customerDepositControl.setValidators([
            Validators.pattern(RegexPatterns.Currency),
            BpValidators.min(0, false),
            control => {
                if (!optInFinanceControl.value) {
                    return null;
                }

                const depositValue = +(control.value || 0);
                const balanceValue = +(balanceControl.value || 0);

                if (balanceValue > WABSLoanLimit) {
                    const requiredDeposit = balanceValue - WABSLoanLimit;
                    if (depositValue < requiredDeposit) {
                        return {
                            insufficientDeposit: {
                                required: requiredDeposit,
                                actual: depositValue,
                                loanLimit: WABSLoanLimit
                            }
                        };
                    }
                }

                return null;
            }
        ]);
    }

    private setupSystemResetLogic(): void {
        const networkAreaChanges$ = this.form.get('networkArea').valueChanges;
        const vppChanges$ = this.form.get('activities.0.vpp').valueChanges;

        merge(networkAreaChanges$, vppChanges$)
            .pipe(debounceTime(10), takeUntil(this.destroyed$))
            .subscribe(() => {
                this.resetSystemAndPriceControls();
            });
    }

    _onSubmit() {
        if (this.form.valid) {
            const formValue = this.form.getRawValue();

            delete formValue.networkArea;

            this.onSubmit.emit({
                formModel: {
                    ...formValue,
                    scope: Scheme.WesternAustralia,
                    installationAddress: {
                        ...formValue.installationAddress,
                        postCode: formValue.installationAddress.postcode
                    }
                },
                selectedPackages: this._localSelectedPackages
            } as QuoteReviewModel);
        } else {
            scrollToFirstInvalidControl(this.form, this.elementRef);
        }
    }

    _onDocumentAvailabilityChange(allDocumentsProvided: boolean) {
        this.allDocumentsProvided = allDocumentsProvided;
    }

    _canApplyForFinance() {
        return !!this.portalConfig;
    }

    _canResendFinanceLink() {
        return (
            this.formMode === QuoteFormCrudMode.View &&
            this.form.get('optInFinance').value &&
            this.quoteStatusService.isFinanceLinkResendable(this.quoteStatus)
        );
    }

    _canCancel() {
        return this.quoteStatusService.isCancelable(this.quoteStatus) && this.actionPermissions.canCancelQuote;
    }

    _canConfirmInstallation() {
        return (
            this.quoteStatusService.isConfirmable(this.quoteStatus) &&
            this.actionPermissions.canConfirmInstallation &&
            this.allDocumentsProvided
        );
    }

    _canEdit() {
        return this.quoteStatusService.isEditable(this.quoteStatus) && this.actionPermissions.canPatchQuote;
    }

    _shouldDisplayBottomActions() {
        return (
            this.formMode !== QuoteFormCrudMode.View ||
            this._canCancel() ||
            this._canConfirmInstallation() ||
            this._canEdit()
        );
    }

    _shouldDisplayTopActions() {
        return (
            (this.formMode !== QuoteFormCrudMode.Create && this._canCancel()) ||
            this._canConfirmInstallation() ||
            this._canEdit()
        );
    }

    _batteryPackageSelected(pkg: CecPackageModel) {
        this._localSelectedPackages.battery = pkg;
        this.packageSelected.emit({ category: 'battery', pkg: pkg });
    }

    _inverterPackageSelected(pkg: CecPackageModel) {
        this._localSelectedPackages.inverter = pkg;
        this.packageSelected.emit({ category: 'inverter', pkg: pkg });
    }

    _getTotalSystemAndInstallationCost(): number {
        const systemCost = +this.form.get('packagePrice').value;
        const installationPrice = +this.form.get('installationPrice').value;
        return installationPrice + systemCost;
    }

    //calculate deposit based on $10,000 WABS loan limit
    _getDepositAmount(): number {
        const systemCost = this._getTotalSystemAndInstallationCost();
        const actualCost = systemCost - this.form.get('activities.0.rebateAmount').value;
        return actualCost > WABSLoanLimit ? actualCost - WABSLoanLimit : 0;
    }

    _getTotalSystemAndInstallationCostGstExclusive(): number {
        const totalSystemCost = this._getTotalSystemAndInstallationCost();
        return totalSystemCost ? new Decimal(totalSystemCost).div(1.1).toNumber() : 0;
    }

    _getUneditableReason() {
        return this.quoteStatusService.notEditableReasonText(this.quoteStatus);
    }
}
